import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, Flag, ChevronLeft, ChevronRight, Check, X, AlertTriangle, Menu, Grid3X3, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Medical License Exam Simulator - 150 questions, 210 minutes
export const MedicalSimulator = ({ userId, onComplete, onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Answer tracking
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  
  // UI state
  const [showNavigator, setShowNavigator] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [language, setLanguage] = useState('he'); // he or en
  
  // Timer - 210 minutes = 12600 seconds
  const [timeLeft, setTimeLeft] = useState(210 * 60);
  const timerRef = useRef(null);

  // Load exam
  useEffect(() => {
    const loadExam = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/timed-exam/medical-license`);
        
        setQuestions(response.data.questions || []);
        setTimeLeft(response.data.duration_minutes * 60);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load medical exam:', err);
        setError('שגיאה בטעינת המבחן');
        setLoading(false);
      }
    };
    
    loadExam();
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer countdown
  useEffect(() => {
    if (loading || questions.length === 0) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, questions.length]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const toggleMarkForReview = (questionId) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const goToQuestion = (index) => {
    setCurrentIndex(index);
    setShowNavigator(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Calculate results
    let correct = 0;
    let wrong = 0;
    let unanswered = 0;
    
    const questionResults = questions.map((q, idx) => {
      const userAnswer = answers[q.id];
      const isCorrect = userAnswer === q.correct_answer;
      
      if (userAnswer === undefined) {
        unanswered++;
      } else if (isCorrect) {
        correct++;
      } else {
        wrong++;
      }
      
      return {
        question_number: idx + 1,
        question_id: q.id,
        user_answer: userAnswer,
        correct_answer: q.correct_answer,
        is_correct: isCorrect,
        topic: q.topic
      };
    });
    
    const results = {
      exam_type: 'medical-license',
      total_questions: questions.length,
      correct,
      wrong,
      unanswered,
      score: Math.round((correct / questions.length) * 100),
      time_used: (210 * 60) - timeLeft,
      question_results: questionResults
    };
    
    setIsSubmitting(false);
    onComplete(results);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-xl">טוען מבחן רישוי רפואה...</p>
          <p className="text-slate-400 mt-2">150 שאלות • 210 דקות</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-white text-xl">{error}</p>
          <button onClick={onBack} className="mt-4 bg-slate-700 text-white px-6 py-2 rounded-lg">
            חזרה
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-950" dir="rtl">
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-lg border-b border-blue-500/20 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Left: Back button */}
          <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2">
            <ChevronRight className="w-5 h-5" />
            יציאה
          </button>

          {/* Center: Timer */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
            timeLeft < 600 ? 'bg-red-500/20 text-red-400' : 
            timeLeft < 1800 ? 'bg-yellow-500/20 text-yellow-400' : 
            'bg-blue-500/20 text-blue-400'
          }`}>
            <Clock className="w-5 h-5" />
            <span className="font-mono text-xl font-bold">{formatTime(timeLeft)}</span>
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-3">
            {/* Language toggle */}
            <button
              onClick={() => setLanguage(lang => lang === 'he' ? 'en' : 'he')}
              className="flex items-center gap-1 px-3 py-2 bg-slate-700 rounded-lg text-white text-sm hover:bg-slate-600"
            >
              <Globe className="w-4 h-4" />
              {language === 'he' ? 'EN' : 'עב'}
            </button>

            {/* Navigator toggle */}
            <button
              onClick={() => setShowNavigator(!showNavigator)}
              className="p-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600"
            >
              <Grid3X3 className="w-5 h-5" />
            </button>

            {/* Question counter */}
            <span className="text-blue-400 font-bold">
              {currentIndex + 1}/{questions.length}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="max-w-6xl mx-auto mt-2">
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{answeredCount} נענו</span>
            <span>{questions.length - answeredCount} נותרו</span>
          </div>
        </div>
      </header>

      {/* Navigator Panel */}
      {showNavigator && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNavigator(false)}>
          <div className="bg-slate-800 rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">ניווט שאלות</h3>
              <button onClick={() => setShowNavigator(false)} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-10 gap-2">
              {questions.map((q, idx) => {
                const isAnswered = answers[q.id] !== undefined;
                const isMarked = markedForReview.has(q.id);
                const isCurrent = idx === currentIndex;
                
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(idx)}
                    className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                      isCurrent ? 'ring-2 ring-blue-500' : ''
                    } ${
                      isMarked ? 'bg-yellow-500 text-black' :
                      isAnswered ? 'bg-green-600 text-white' : 
                      'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            
            <div className="flex gap-4 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-600 rounded"></div>
                <span className="text-slate-300">נענה</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-slate-300">מסומן לבדיקה</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-700 rounded"></div>
                <span className="text-slate-300">לא נענה</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        {currentQuestion && (
          <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/10">
            {/* Question header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  שאלה {currentIndex + 1}
                </span>
                {currentQuestion.topic && (
                  <span className="bg-slate-700 text-slate-300 px-3 py-1 rounded-full text-sm">
                    {currentQuestion.topic}
                  </span>
                )}
              </div>
              <button
                onClick={() => toggleMarkForReview(currentQuestion.id)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  markedForReview.has(currentQuestion.id)
                    ? 'bg-yellow-500 text-black'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <Flag className="w-4 h-4" />
                סמן
              </button>
            </div>

            {/* Question text */}
            <div className="mb-8">
              <p className="text-white text-lg leading-relaxed whitespace-pre-line">
                {language === 'he' ? currentQuestion.text_he : currentQuestion.text_en}
              </p>
              {/* Show other language as hint */}
              {language === 'he' && currentQuestion.text_en && (
                <p className="text-slate-500 text-sm mt-3 border-t border-slate-700 pt-3" dir="ltr">
                  {currentQuestion.text_en}
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3">
              {(language === 'he' ? currentQuestion.options_he : currentQuestion.options_en || currentQuestion.options_he)?.map((option, idx) => {
                const isSelected = answers[currentQuestion.id] === idx;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(currentQuestion.id, idx)}
                    className={`w-full text-right p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500/20 text-white'
                        : 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500 hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {String.fromCharCode(1488 + idx)}
                      </span>
                      <span className="flex-1">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold ${
              currentIndex === 0
                ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                : 'bg-slate-700 text-white hover:bg-slate-600'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
            הקודם
          </button>

          {/* Submit button (show after last question) */}
          {currentIndex === questions.length - 1 ? (
            <button
              onClick={() => setShowConfirmSubmit(true)}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:opacity-90"
            >
              סיום והגשה
            </button>
          ) : (
            <button
              onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
              className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-400"
            >
              הבא
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>
      </main>

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">סיום המבחן?</h3>
            <p className="text-slate-400 mb-6">
              ענית על {answeredCount} מתוך {questions.length} שאלות.
              {questions.length - answeredCount > 0 && (
                <span className="block text-yellow-400 mt-2">
                  {questions.length - answeredCount} שאלות לא נענו!
                </span>
              )}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl font-bold hover:bg-slate-600"
              >
                חזור למבחן
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-400 disabled:opacity-50"
              >
                {isSubmitting ? 'שולח...' : 'הגש מבחן'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalSimulator;
